import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import EditIcon from "@mui/icons-material/Edit";
import ListIcon from "@mui/icons-material/List";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useActions, { useAppSelector } from "../../../app/hooks";

import { selectLibrary } from "../../library/librarySlice";
import { Project } from "../../../types/Project";
import ProjectModal from "./ProjectModal";

export default function ProjectsList(props: {
  parentCloseHandle: () => void;
}) {
  const actions = useActions();

  const [createMode, setCreateMode] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [selectedProject, setSelectedProject] = React.useState<Project | null>(null);
  const [projectModalProject, setProjectModalProject] = React.useState<Project | null>(null);

  const [name, setName] = useState("");
  const [nameError, setNameError] = React.useState("");

  const librarySlice = useAppSelector(selectLibrary);

  useEffect(() => {
    actions.getProjects();
  }, []);

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value.trim() === "") {
      setNameError("Invalid name");
    } else setNameError("");
    setName(e.target.value);
  }

  function resetFormFields() {
    setName("");
    setNameError("");
    setCreateMode(false);
    setEditMode(false);
    setSelectedProject(null);
  }

  function handleCreateProject() {
    actions.createProject({ name: name.trim() });
    resetFormFields();
  }

  function handleEditProject(project: Project) {
    setSelectedProject(project);
    setEditMode(true);
    setName(project.name || "");
  }

  function handleUpdateProject() {
    if (selectedProject) {
      actions.updateProject({
        projectId: selectedProject.id,
        projectUpdateRequest: { name: name.trim() },
      });
      resetFormFields();
    }
  }

  return (
    <Dialog open={true} onClose={() => props.parentCloseHandle()} maxWidth="sm" fullWidth>
      <DialogTitle>Projects</DialogTitle>

      <DialogContent>
        {!createMode && !editMode && (
          <Box sx={{ pt: 2 }}>
            {librarySlice.projects.length === 0 && (
              <Typography sx={{ mb: 2, textAlign: "center" }}>
                No projects in the workspace...
              </Typography>
            )}

            <List>
              {librarySlice.projects.map((project) => (
                <ListItem
                  key={"project_deets_" + project.id}
                  sx={{ py: 1.5 }}
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        edge="end"
                        onClick={() => setProjectModalProject(project)}
                        size="small"
                        title="View tasks"
                      >
                        <ListIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditProject(project)}
                        size="small"
                        title="Edit Project"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText primary={project.name} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {createMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Create a new project</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Project name"
              error={nameError !== ""}
              helperText={nameError}
              fullWidth
              variant="standard"
              value={name}
              onChange={handleNameChange}
              sx={{ mb: 2 }}
            />
          </Box>
        )}
        {editMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Edit project</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Project name"
              error={nameError !== ""}
              helperText={nameError}
              fullWidth
              variant="standard"
              value={name}
              onChange={handleNameChange}
              sx={{ mb: 2 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={() => {
            if (createMode || editMode) {
              resetFormFields();
            } else {
              props.parentCloseHandle();
            }
          }}
        >
          Cancel
        </Button>
        {!createMode && !editMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateMode(true)}
          >
            Create Project
          </Button>
        )}
        {createMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleCreateProject()}
          >
            Confirm
          </Button>
        )}
        {editMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleUpdateProject()}
          >
            Update
          </Button>
        )}
      </DialogActions>

      <ProjectModal
        open={projectModalProject !== null}
        project={projectModalProject}
        onClose={() => setProjectModalProject(null)}
      />
    </Dialog>
  );
}
